import { memo, useMemo } from 'react'
import { SoundWaveIcon } from './Icons'
import { translationDevStyles } from './transcript-stream-styles'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useSequentialBatchReveal } from '@/hooks/useSequentialBatchReveal'
import { getSpeakerColor } from '@/utils/speaker-colors'
import { formatElapsedRange } from '@/utils/formatElapsedTime'
import type { LiveTranscriptTurn } from '@/utils/liveTranscriptTurns'

interface BilingualTimelineProps {
  turns: LiveTranscriptTurn[]
  /**
   * The newest turn's translated line reads a couple of pixels larger. False in
   * Meeting mode, where a lead panel whose text resizes as turns arrive is just
   * movement (BRAI-416). The tint on that row is unaffected either way.
   * Defaults true so existing callers keep the interview behaviour.
   */
  enlargeNewestTranslation?: boolean
}

interface TimelineTurnRowProps {
  turn: LiveTranscriptTurn
  revealedSourceText: string | undefined
  sourceIsFinal: boolean
  revealedTranslationText: string | undefined
  translationIsFinal: boolean
  isActive: boolean
  /** Part of the newest block in the pane, so this row wears the tint. */
  isNewest: boolean
  /** See `BilingualTimelineProps.enlargeNewestTranslation`. */
  enlargeNewestTranslation: boolean
  /**
   * False for the continuation rows of a speaker block, which run straight on
   * from the row above under its single heading.
   */
  showHeader: boolean
  /** Start of the whole block, so the heading spans it rather than this row. */
  blockStartMs: number | undefined
  /** End of the whole block, extended by each event that joins it. */
  blockEndMs: number | undefined
}

/** One run of adjacent turns that Enhanced Mode shows as a single block. */
interface TimelineBlock {
  key: string
  /** Indexes into the filtered turn list, in timeline order. */
  indexes: number[]
  startMs: number | undefined
  endMs: number | undefined
}

/**
 * Collects adjacent turns that share a `speakerBlockId` into one block, so a run
 * of `translation.native_non_streaming` events for one speaker reads as
 * continuous speech instead of a card per event.
 *
 * Only adjacent turns are grouped: an out-of-order arrival landing between two
 * turns of a block splits it rather than being swallowed into the middle. Turns
 * with no block id — every other event kind — each stand alone, which is what
 * keeps their rendering exactly as it was.
 *
 * @param turns Filtered, timeline-ordered turns backing the pane.
 * @returns One entry per block, carrying its member indexes and time span.
 */
function groupTurnsIntoBlocks(turns: LiveTranscriptTurn[]): TimelineBlock[] {
  const blocks: TimelineBlock[] = []

  turns.forEach((turn, index) => {
    const open = blocks[blocks.length - 1]
    if (open !== undefined && turn.speakerBlockId !== undefined && open.key === turn.speakerBlockId) {
      open.indexes.push(index)
      // Start stays with the first event of the block; end follows the latest.
      open.startMs = open.startMs ?? turn.startMs
      open.endMs = turn.endMs ?? open.endMs
      return
    }
    blocks.push({
      key: turn.speakerBlockId ?? turn.id,
      indexes: [index],
      startMs: turn.startMs,
      endMs: turn.endMs,
    })
  })

  return blocks
}

/**
 * Renders one compact batch-backed transcript row from its already-revealed
 * text (see `useSequentialBatchReveal`). The row itself mounts as soon as the
 * turn arrives — `animate-segment-in` slides it in, and the newest row holds a
 * green tint until a newer turn takes it over, so the front of a pane that
 * keeps growing is always obvious — while its source/translation text streams
 * in word by word. Memoized on primitive props so a completed row never
 * re-renders while a later row is still animating; that memo is what keeps the
 * reveal a one-off.
 *
 * Handing the tint on costs exactly two re-renders — the row losing it and the
 * row taking it — and neither replays the slide-in, because the tint is a
 * `transition` on its own class and `animate-segment-in` stays put (see
 * `index.css`).
 *
 * Deliberately NOT using `content-visibility: auto` here even though it would
 * skip layout for offscreen rows — while the tab is backgrounded the browser
 * treats none of them as "relevant" and skips layout for rows that arrive
 * meanwhile too, so `scrollHeight` undercounts real content and auto-scroll
 * can't reach the true bottom once the tab is foregrounded again.
 *
 * @param props Turn metadata plus the revealed source/translation prefixes.
 */
const TimelineTurnRow = memo(function TimelineTurnRow({
  turn,
  revealedSourceText,
  sourceIsFinal,
  revealedTranslationText,
  translationIsFinal,
  isActive,
  isNewest,
  enlargeNewestTranslation,
  showHeader,
  blockStartMs,
  blockEndMs,
}: TimelineTurnRowProps) {
  const { t } = useUiStrings()
  const speakerColor = getSpeakerColor(turn.speaker ?? '—')
  const speakerLabel = turn.speakerName ?? (turn.speaker ? `Speaker ${turn.speaker}` : undefined)

  // Timestamp sits beside the speaker name (same flex pattern as auto-detect
  // `SpeakerLabel`), while still using `formatElapsedRange` so a turn can show
  // `mm:ss – mm:ss` instead of start-only.
  return (
    <article
      className={[
        'min-w-0 py-1 text-xs leading-5 animate-segment-in sm:text-sm',
        isNewest ? 'segment-highlight-on' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`timeline-turn-${turn.id}`}
    >
      {showHeader ? (
        <div className="mb-0.5 flex min-w-0 items-baseline gap-5 text-xs font-bold uppercase sm:text-sm">
          <span className="truncate" style={{ color: speakerColor }}>
            {speakerLabel ?? t.unknownSpeaker}
          </span>
          {blockStartMs !== undefined ? (
            <time className="whitespace-nowrap text-xs font-normal normal-case tabular-nums text-slate-500 dark:text-[var(--text-tertiary)]">
              {formatElapsedRange(blockStartMs, blockEndMs)}
            </time>
          ) : null}
        </div>
      ) : null}
      {turn.sourceText ? (
        <section aria-label={t.originalTranscript}>
          {revealedSourceText !== undefined ? (
            <p
              className={[
                'whitespace-pre-wrap break-words text-xs leading-5 sm:text-sm',
                sourceIsFinal
                  ? 'text-slate-900 dark:text-[var(--text-primary)]'
                  : 'text-slate-500 dark:text-[var(--text-secondary)]',
              ].join(' ')}
            >
              {revealedSourceText}
            </p>
          ) : (
            <span
              className="inline-block h-4 w-16 animate-pulse rounded bg-slate-100 dark:bg-slate-300/10"
              aria-hidden
            />
          )}
        </section>
      ) : null}

      {turn.batchTranslation ? (
        <section className="mt-0.5" aria-label={t.batchTranslation}>
          {revealedTranslationText !== undefined ? (
            <p
              className={[
                'whitespace-pre-wrap break-words',
                // Marks the line the tint enlarges. Applied whether or not the
                // translation has finalized, so the text does not change size
                // partway through revealing. `index.css` scopes the size to a
                // tinted row; here it is only a handle — so withholding it is
                // how Meeting mode drops the size bump without touching the
                // tint (BRAI-416).
                enlargeNewestTranslation ? 'segment-translation-text' : '',
                translationIsFinal
                  ? translationDevStyles.finalTokenClass
                  : 'text-slate-500 dark:text-[var(--text-secondary)]',
              ].join(' ')}
              aria-live={isActive ? 'polite' : 'off'}
            >
              {revealedTranslationText}
            </p>
          ) : (
            <span
              className="inline-block h-4 w-16 animate-pulse rounded bg-indigo-100 dark:bg-indigo-300/10"
              aria-hidden
            />
          )}
        </section>
      ) : null}
    </article>
  )
})

/**
 * Displays finalized batch translations in one chronological bilingual
 * conversation, with smaller source text for context. Batches reveal strictly
 * one at a time; within each batch the source and translation stream in sync
 * (start and finish together). See `useSequentialBatchReveal`.
 *
 * Turns carrying a `speakerBlockId` are drawn as one block — a single speaker
 * heading, one timestamp spanning the whole run, and no gap between the rows —
 * so a speaker holding the floor across several
 * `translation.native_non_streaming` events reads as continuous speech. Every
 * other event kind is a block of one, drawn exactly as before.
 *
 * @param turns Source-aligned turn snapshots from the recording hook.
 */
export default function BilingualTimeline({
  turns,
  enlargeNewestTranslation = true,
}: BilingualTimelineProps) {
  const { t } = useUiStrings()
  const batchTurns = useMemo(
    () => turns.filter((turn) => Boolean(turn.batchTranslation)),
    [turns]
  )
  const revealed = useSequentialBatchReveal(batchTurns)
  const blocks = useMemo(() => groupTurnsIntoBlocks(batchTurns), [batchTurns])

  if (batchTurns.length === 0) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center px-4 py-6 text-center sm:px-6 sm:py-10">
        <div
          className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--accent-light)] text-[var(--accent)]"
          aria-hidden
        >
          <SoundWaveIcon className="h-6 w-6" />
        </div>
        <p className="text-xs font-semibold text-[var(--text-primary)] sm:text-sm">
          {t.enhancedWaitingTitle}
        </p>
        <p className="mt-1.5 max-w-sm text-xs leading-5 text-[var(--text-secondary)]">
          {t.enhancedWaitingBody}
        </p>
      </div>
    )
  }

  // The reveal cascade shows one turn at a time, so a block can be partly
  // rendered while its later events are still queued.
  const newestIndex = revealed.length - 1

  return (
    <div className="space-y-0.5" aria-label={t.bilingualTimeline}>
      {blocks.map((block) => {
        const visibleIndexes = block.indexes.filter((index) => index < revealed.length)
        if (visibleIndexes.length === 0) return null

        // The tint marks the block, not the row: a run of speech reads as one
        // unit, so handing the mark to its latest row alone would split the
        // block visually right where it is meant to be continuous.
        const isNewestBlock = block.indexes.includes(newestIndex)

        // No vertical gap inside a block — the rows share one left border and
        // must not be broken apart by the `space-y` between blocks.
        return (
          <div key={block.key}>
            {visibleIndexes.map((index, position) => {
              const turn = batchTurns[index]
              const reveal = revealed[index]
              return (
                <TimelineTurnRow
                  key={turn.id}
                  turn={turn}
                  revealedSourceText={reveal.revealedSourceText}
                  sourceIsFinal={reveal.sourceIsFinal}
                  revealedTranslationText={reveal.revealedTranslationText}
                  translationIsFinal={reveal.translationIsFinal}
                  isActive={reveal.isActive}
                  // The block itself, not the reveal: a block is "the new one"
                  // from the moment it lands, while its text is still arriving.
                  isNewest={isNewestBlock}
                  enlargeNewestTranslation={enlargeNewestTranslation}
                  showHeader={position === 0}
                  blockStartMs={block.startMs}
                  blockEndMs={block.endMs}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
